import { useCallback, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import Divider from "@mui/material/Divider";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import CheckIcon from "@mui/icons-material/Check";
import { useLang } from "../i18n/LangContext";
import { useAppState } from "../state/AppState";
import { SettingsMenu } from "./SettingsMenu";
import { NAV_ROUTES, shouldHideMapCta } from "../routes.config";
import type { Currency, Lang } from "../types";

const LANGS: { value: Lang; label: string }[] = [
  { value: "de", label: "Deutsch" },
  { value: "en", label: "English" },
  { value: "is", label: "Íslenska" },
];
const CURRENCIES: Currency[] = ["ISK", "EUR", "USD", "CHF", "GBP"];

export function Topbar() {
  const { t, lang, setLang } = useLang();
  const { currency, setCurrency } = useAppState();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  const isActive = (to: string) => {
    if (to === "/") return location.pathname === "/";
    if (to === "/map")
      return location.pathname === "/map" || location.pathname.startsWith("/spot/");
    return location.pathname === to || location.pathname.startsWith(to + "/");
  };

  const hideMapCta = shouldHideMapCta(location.pathname);

  return (
    <>
      <header className="topbar" role="banner">
        <Link to="/" className="topbar__brand" aria-label="Fishing Iceland — home">
          <span className="topbar__logo" aria-hidden="true">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.8}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M2 12c3-4 6-6 11-6 4 0 7 2 9 6-2 4-5 6-9 6-5 0-8-2-11-6Z" />
              <path d="M14 9.5l3-2.5M14 14.5l3 2.5" />
              <circle cx="17" cy="11.5" r=".7" fill="currentColor" />
            </svg>
          </span>
          <span className="topbar__title">Fishing Iceland</span>
        </Link>
        <nav className="topbar__nav" aria-label="Primary">
          {NAV_ROUTES.filter((r) => r.path !== "/map").map((r) => (
            <NavLink
              key={r.path}
              to={r.path}
              className={`topbar__link${isActive(r.path) ? " topbar__link--active" : ""}`}
              end={r.path === "/"}
            >
              {r.labelKey ? t(r.labelKey) : r.path}
            </NavLink>
          ))}
        </nav>
        <div className="topbar__actions">
          {!hideMapCta && (
            <Button
              component={Link}
              to="/map"
              variant="contained"
              color="primary"
              size="small"
              startIcon={<MapOutlinedIcon />}
              className="topbar__cta"
            >
              {t("navMap")}
            </Button>
          )}
          {/* Desktop keeps a quick settings cog for lang/currency; on
              mobile the same controls live inside the burger drawer. */}
          <span className="topbar__settings-desktop">
            <SettingsMenu />
          </span>
          <IconButton
            className="topbar__burger"
            aria-label={t("menu")}
            onClick={openDrawer}
            size="small"
            sx={{ display: { xs: "inline-flex", md: "none" }, color: "text.primary" }}
          >
            <MenuIcon />
          </IconButton>
        </div>
      </header>
      <SwipeableDrawer
        anchor="right"
        open={drawerOpen}
        onClose={closeDrawer}
        onOpen={openDrawer}
        disableSwipeToOpen
        PaperProps={{
          sx: {
            bgcolor: "background.paper",
            backgroundImage: "none",
            width: { xs: "80vw", sm: 320 },
            maxWidth: 360,
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            pt: "calc(env(safe-area-inset-top, 0px) + 8px)",
            pb: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
          },
        }}
      >
        <div className="topbar__drawer-head">
          <span className="topbar__title">Fishing Iceland</span>
          <IconButton
            aria-label={t("close")}
            onClick={closeDrawer}
            size="small"
            sx={{ color: "text.primary" }}
          >
            <CloseIcon />
          </IconButton>
        </div>
        <nav className="topbar__drawer-nav" aria-label="Mobile primary">
          {NAV_ROUTES.map((r) => (
            <NavLink
              key={r.path}
              to={r.path}
              onClick={closeDrawer}
              className={`topbar__drawer-link${isActive(r.path) ? " topbar__drawer-link--active" : ""}`}
              end={r.path === "/"}
            >
              {r.labelKey ? t(r.labelKey) : r.path}
            </NavLink>
          ))}
          <a
            className="topbar__drawer-link topbar__drawer-link--external"
            href="mailto:hi@fishing-iceland.app?subject=Feedback%20Fishing%20Iceland"
            onClick={closeDrawer}
          >
            {t("feedbackLink")}
          </a>
        </nav>
        <Divider sx={{ my: 1, borderColor: "rgba(255,255,255,0.08)" }} />
        <div className="topbar__drawer-section-label">{t("language")}</div>
        <div className="topbar__drawer-pills" role="group" aria-label={t("language")}>
          {LANGS.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              className={`topbar__pill${lang === value ? " topbar__pill--active" : ""}`}
              onClick={() => setLang(value)}
            >
              {lang === value && <CheckIcon fontSize="small" />}
              <span>{label}</span>
            </button>
          ))}
        </div>
        <div className="topbar__drawer-section-label">{t("currency")}</div>
        <div className="topbar__drawer-pills" role="group" aria-label={t("currency")}>
          {CURRENCIES.map((c) => (
            <button
              key={c}
              type="button"
              className={`topbar__pill${currency === c ? " topbar__pill--active" : ""}`}
              onClick={() => setCurrency(c)}
            >
              {currency === c && <CheckIcon fontSize="small" />}
              <span>{c}</span>
            </button>
          ))}
        </div>
      </SwipeableDrawer>
    </>
  );
}
